import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModal } from "../../redux/modalSlice";
import { useRequest } from "../../hooks/UseRequest";
import Loading from "../../components/Loading";

/** ---------- utils ---------- */
function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
function fmtHMS(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${s}s (${h}:${m}:${ss})`;
}
function uniqueKey(parts) {
  return parts.filter(Boolean).join("_");
}

/** ---------- presentational tile ---------- */
function SummaryItem({ label, value, mono, onCopy }) {
  return (
    <div className={`tile ${mono ? "mono" : ""}`}>
      <p className="label">{label}</p>
      <p className="value" title={typeof value === "string" ? value : undefined}>
        {value || "—"}
      </p>
      {onCopy && (
        <button className="button is-small" onClick={onCopy} type="button" title="Copy value">
          Copy
        </button>
      )}
    </div>
  );
}

/** ================================================================
 *  MAIN COMPONENT
 *  ================================================================ */
export default function CallNoteModal() {
  const modal = useSelector((state) => state.modal);
  const note = modal.options?.note;
  const dispatch = useDispatch();
  const request = useRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [callData, setCallData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClose = () => {
    dispatch(setModal({ is_active: false }));
  };

  useEffect(() => {
    if (modal.is_active && modal.type === "call_note_modal" && note?.ext_call_sid && note?.id) {
      getCallNote();
    } else {
      setCallData(null);
    }
  }, [modal.is_active, modal.type, note?.ext_call_sid, note?.id]);

  const getCallNote = async () => {
    setIsLoading(true);
    setErrorMsg("");
    request.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/customer_note/${note.id}/dialer_data`
    );
    try {
      const response = await request.get();
      const raw = response?.data?.data;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      setCallData(parsed ?? null);
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        error?.response?.data?.messageExtreme ??
        "Failed to load call data.";
      setErrorMsg(message);
      setCallData(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (!modal.is_active || modal.type !== "call_note_modal") return null;

  return (
    <div className="modal is-active">
      <div className="modal-background" />
      <div className="modal-card" style={{ width: 1100, maxWidth: "95vw" }}>
        <header className="modal-card-head py-3">
          <p className="modal-card-title">Dialer Call Note</p>
          <button
            className="delete"
            aria-label="close"
            onClick={handleClose}
            type="button"
          />
        </header>

        <section className="modal-card-body">
          <div id="caller_data_customer_note">
            {isLoading && <Loading />}
            {!isLoading && errorMsg && (
              <p className="has-text-danger">{errorMsg}</p>
            )}
            {!isLoading && callData && <CallDataView data={callData} />}
            {!isLoading && !errorMsg && !callData && (
              <p className="has-text-grey">No call data.</p>
            )}
          </div>
        </section>

        <footer className="modal-card-foot is-justify-content-end py-2 pr-2">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button
              type="button"
              className="button is-small has-background-grey has-text-white"
              onClick={handleClose}
            >
              Done
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/** ---------- main content view ---------- */
function CallDataView({ data }) {
  const hasMedia = data?.media_data != null;
  const mediaData = data?.media_data ?? {};
  const interaction = mediaData?.interactions?.[0] ?? {};
  const iData = interaction?.data ?? {};
  const contact = data?.contact_data?.contactId ?? {};

  const meta = useMemo(() => {
    const segments = iData?.segmentsDataList || interaction?.segmentsDataList || [];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";
    return {
      transferLogId: data?.transfer_log_id ?? "",
      elevated: Boolean(mediaData?.elevatedInteraction),
      media: interaction?.mediaType || "",
      channel: interaction?.channelType || segments?.[0]?.channelType || "",
      tz,
    };
  }, [data]);

  const participants = iData?.participantDataList ?? [];
  const segments = iData?.segmentsDataList ?? interaction?.segmentsDataList ?? [];
  const fileUrl = iData?.fileToPlayUrl || null;
  const poster = iData?.videoImageUrl || null;

  return (
    <div className="content">

      {/* ── header badges ── */}
      <div className="card-block">
        <div className="row">
          {Object.keys(contact).length > 0 && (
            <span className={`badge ${contact.isOutbound ? "out" : "in"}`} title="Direction">
              {contact.isOutbound ? "Outbound" : "Inbound"}
            </span>
          )}
          {hasMedia ? (
            <>
              <div>
                <div className="is-size-7 has-text-grey" id="meta-line">
                  {[meta.media?.toLowerCase(), meta.channel?.toLowerCase()]
                    .filter(Boolean)
                    .join(" • ") || "call"}
                </div>
              </div>
              <span className={`badge ${meta.elevated ? "elev" : "nelev"}`} title="Elevated">
                {meta.elevated ? "Elevated" : "Not Elevated"}
              </span>
              <span className="badge" title="Timezone">{meta.tz}</span>
            </>
          ) : (
            <span className="has-text-grey is-size-7">No recording found</span>
          )}
          {meta.transferLogId ? (
            <a
              href={`/rc_transfer_log/${meta.transferLogId}`}
              className="badge transfer_log_id"
              title="Transfer Log Id"
              target="_blank"
              rel="noopener noreferrer"
            >
              Transfer Log ID: #{meta.transferLogId}
            </a>
          ) : ""}
        </div>
      </div>

      {/* ── contact details ── */}
      {Object.keys(contact).length > 0 && (
        <div className="card-block">
          <div className="title is-6 mb-2">Contact Details</div>
          <div className="grid">
            <SummaryItem
              label="Contact ID"
              value={contact.contactId ? String(contact.contactId) : "—"}
              mono
              onCopy={() => navigator.clipboard?.writeText(String(contact.contactId || ""))}
            />
            <SummaryItem label="Campaign" value={contact.campaignName} />
            <SummaryItem label="Skill" value={contact.skillName} />
            <SummaryItem label="Point of Contact" value={contact.pointOfContactName} />
            <SummaryItem label="From" value={contact.fromAddress} />
            <SummaryItem label="To" value={contact.toAddress} />
            <SummaryItem label="Start" value={fmtDT(contact.contactStartDate)} />
            <SummaryItem label="End Reason" value={contact.endReason} />
            <SummaryItem label="State" value={contact.stateName} />
            <SummaryItem label="Total Duration" value={fmtHMS(contact.totalDurationSeconds)} />
            <SummaryItem label="Agent Time" value={fmtHMS(contact.agentSeconds)} />
            <SummaryItem
              label="Hold"
              value={
                contact.holdSeconds != null
                  ? `${fmtHMS(contact.holdSeconds)} × ${contact.holdCount ?? 0}`
                  : "—"
              }
            />
            <SummaryItem label="ACW" value={fmtHMS(contact.acwSeconds)} />
            <SummaryItem label="Transfer" value={contact.transferIndicatorName} />
            <SummaryItem label="Media Type" value={contact.mediaTypeName} />
            <SummaryItem label="Active" value={contact.isActive ? "Yes" : "No"} />
            <SummaryItem label="Recorded" value={contact.isLoggedRecording ? "Yes" : "No"} />
          </div>
        </div>
      )}

      {/* ── participants ── */}
      {hasMedia && participants.length > 0 && (
        <div className="card-block">
          <div className="title is-6 mb-2">Participants</div>
          <div className="table-wrap">
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name / ID</th>
                  <th>Channel</th>
                  <th>Voice Stages</th>
                  <th>Screen Stages</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.participantId || p.agentName || uniqueKey([p.participantType, p.channel])}>
                    <td>{p.participantType || "—"}</td>
                    <td>
                      {p.agentName && <span>{p.agentName} • </span>}
                      {p.participantId ? <code>{p.participantId}</code> : "—"}
                    </td>
                    <td>{p.channel ?? "—"}</td>
                    <td>
                      {(p.voiceStages || []).length
                        ? p.voiceStages.map((s) => (
                            <div key={uniqueKey([p.participantId, "voice", s.startTime])}>
                              <strong>{s.stageType || "ACTIVE"}:</strong>{" "}
                              {fmtDT(s.startTime)} → {fmtDT(s.endTime)}
                            </div>
                          ))
                        : "—"}
                    </td>
                    <td>
                      {(p.screenStages || []).length
                        ? p.screenStages.map((s) => (
                            <div key={uniqueKey([p.participantId, "screen", s.startTime])}>
                              <strong>{s.stageType || "ACTIVE"}:</strong>{" "}
                              {fmtDT(s.startTime)} → {fmtDT(s.endTime)}
                            </div>
                          ))
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── segments ── */}
      {hasMedia && segments.length > 0 && (
        <div className="card-block">
          <div className="title is-6 mb-2">Segments</div>
          <div className="table-wrap">
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th>Segment ID</th>
                  <th>Direction</th>
                  <th>Channel</th>
                  <th>Open / Close</th>
                  <th>Source</th>
                  <th>Start → End</th>
                  <th>ACW End</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((s) => (
                  <tr key={s.segmentId || uniqueKey([s.startTime, s.endTime, "seg"])}>
                    <td><code>{s.segmentId || "—"}</code></td>
                    <td>{s.directionType || "—"}</td>
                    <td>{s.channelType || "—"}</td>
                    <td>{(s.openReasonType || "—") + " / " + (s.closeReasonType || "—")}</td>
                    <td>{s.originalSourceFromSdr || s.source || "—"}</td>
                    <td>{fmtDT(s.startTime)} → {fmtDT(s.endTime)}</td>
                    <td>{fmtDT(s.acwEndTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── recording ── */}
      {hasMedia && fileUrl && (
        <div className="card-block">
          <div className="title is-6 mb-2">Recording</div>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video controls preload="metadata" src={fileUrl} poster={poster || undefined} />
        </div>
      )}

    </div>
  );
}
